/**
 * SSE hook for agent chat
 * Handles real-time communication with the agent service.
 * Sends the full AgentContextPayload (tiers 1 + 2) with each message.
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { tokenManager } from '../lib/api';
import type { AgentContextPayload } from '../contexts/AgentContext';

/** User turn the retired interview accessory's silent ping persisted (hidden on reload). */
const LEGACY_INTERVIEW_PING = '(interview answer submitted)';

export type ContentSegment =
  | { type: 'text'; text: string }
  | { type: 'tool_call'; toolCall: ToolCall };

/**
 * Payload of a `conversation_event` SSE message. Emitted by Django signals
 * on ResearchJob terminal transitions and forwarded by the Node agent's
 * ConversationBus. Also persisted as role='event' AgentMessage rows so the
 * cards survive a page reload.
 */
export interface ConversationEventPayload {
  type: string;
  job_id?: string;
  kind?: string;
  kind_display?: string;
  status?: string;
  status_display?: string;
  summary?: string;
  target_type?: string | null;
  target_id?: number | null;
  occurred_at?: string | null;
  [key: string]: unknown;
}

/**
 * Progress snapshot for a pending (non-terminal) ResearchJob. Populated from
 * the conversation-detail `pending_research_jobs` array on load and updated
 * live via `research_job_progress` events on the same SSE pipe as terminal
 * events. Removed from state when the matching `research_job_update` event
 * arrives (terminal transition).
 */
export interface PendingJob {
  jobId: string;
  kind: string;
  kindDisplay: string;
  status: string;
  currentStep: number;
  totalSteps: number;
  currentStepLabel: string;
  startedAt: string | null;
  /**
   * Fine-grained progress (tech-discovery only). Populated from the legacy
   * DiscoveryJob's diligence counters, which the worker updates throughout
   * step 5 (vendor + product enrichment). When `diligenceTotal > 0` the
   * card shows `N/M tasks` instead of the coarse `current_step/total_steps`.
   */
  diligenceCompleted?: number;
  diligenceTotal?: number;
}

export interface AgentMessage {
  id: string;
  role: 'user' | 'assistant' | 'event';
  content: string;
  timestamp: string;
  toolCalls?: ToolCall[];
  segments?: ContentSegment[];
  editIds?: string[];
  isStreaming?: boolean;
  attachments?: string[];
  /** Populated for role='event' messages — structured event card payload. */
  eventPayload?: ConversationEventPayload;
}

export interface ToolCall {
  id: string;
  tool: string;
  params: Record<string, any>;
  result?: any;
  error?: string;
  /**
   * Set by the node service from the tool's own `hideFromTranscript` flag:
   * this tool's work is already shown another way, so its activity pill is
   * duplicate chrome. It rides both the SSE frame and the persisted
   * `tool_calls` row, so the reloaded transcript hides exactly what the live
   * stream hid.
   */
  hidden?: boolean;
}

export interface UndoInfo {
  editId: string;
  description: string;
}

export interface ViewSuggestion {
  view: string;
  params: Record<string, any>;
  autoNavigate?: boolean;
  label?: string;
}

export interface ActionSuggestion {
  action: string;
  description: string;
  params: Record<string, any>;
  label: string;
  endpoint: string;
  method: 'POST' | 'PUT' | 'PATCH';
}

/**
 * The program context document a chat upload was promoted to. Present once the
 * conversation is bound to a program and the upload is flagged
 * `use_as_program_context` (and its text was extractable).
 */
export interface UploadProgramContextDocument {
  id: number;
  job_id: string | null;
  /** Program-wide extraction state; `null` before a job exists. */
  extraction_state: 'running' | 'completed' | 'failed' | string | null;
}

export interface AgentUpload {
  id: string;
  title: string;
  content_summary: string;
  mime_type: string;
  size_bytes: number;
  content_length: number;
  uploaded_at: string;
  /**
   * Whether this file feeds the program (promoted to a ProgramContextDocument
   * and extracted into requirements) or stays chat-only. Backend default true.
   */
  use_as_program_context?: boolean;
  program_context_document?: UploadProgramContextDocument | null;
}

/** True while a promoted upload's program-wide extraction is still running. */
export const isUploadExtracting = (upload: AgentUpload): boolean => {
  const doc = upload.program_context_document;
  if (!doc) return false;
  const state = doc.extraction_state;
  if (state === 'completed' || state === 'failed') return false;
  return state != null || doc.job_id != null;
};

/** Merge server uploads into local state by id (server copy wins). */
const mergeUploads = (local: AgentUpload[], incoming: AgentUpload[]): AgentUpload[] => {
  const byId = new Map(local.map((u) => [u.id, u]));
  for (const u of incoming) byId.set(u.id, u);
  return Array.from(byId.values());
};

interface UseAgentChatOptions {
  conversationId?: string | null;
  /** Called to build the context payload at the moment a message is sent */
  buildContextPayload?: () => AgentContextPayload;
  onViewSuggestion?: (suggestion: ViewSuggestion) => void;
  onError?: (error: string) => void;
  onConversationCreated?: (conversationId: string) => void;
}

const API_URL = import.meta.env.VITE_API_URL || '';

export const useAgentChat = (options: UseAgentChatOptions = {}) => {
  const { conversationId, buildContextPayload, onViewSuggestion, onError, onConversationCreated } = options;

  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [viewSuggestions, setViewSuggestions] = useState<ViewSuggestion[]>([]);
  const [actionSuggestions, setActionSuggestions] = useState<ActionSuggestion[]>([]);
  const [uploads, setUploads] = useState<AgentUpload[]>([]);
  const [pendingJobs, setPendingJobs] = useState<PendingJob[]>([]);

  const streamingMessageRef = useRef<string>('');
  const segmentsRef = useRef<ContentSegment[]>([]);
  const streamingIdRef = useRef<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const pendingUploadFilesRef = useRef<File[]>([]);

  /**
   * Close any active SSE connection
   */
  const closeStream = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  /**
   * Send a message to the agent.
   * Accepts optional files to upload atomically after conversation creation.
   */
  const sendMessage = useCallback(
    async (message: string, context?: Record<string, any>, scriptId?: string, files?: File[]) => {
      if (!message.trim()) return;

      try {
        setIsStreaming(true);

        // Clear any existing suggestions when starting new message
        setViewSuggestions([]);
        setActionSuggestions([]);

        // Build user message content with file attachment indicator
        const fileNames = files && files.length > 0
          ? files.map((f) => f.name)
          : [];

        // Add user message to UI immediately (with file names if any)
        const userMessage: AgentMessage = {
          id: `user-${Date.now()}`,
          role: 'user',
          content: message,
          timestamp: new Date().toISOString(),
          attachments: fileNames.length > 0 ? fileNames : undefined,
        };
        console.log('💬 Adding user message to UI:', userMessage);
        setMessages((prev) => [...prev, userMessage]);

        // Build full context payload (tiers 1 + 2)
        const contextPayload = buildContextPayload ? buildContextPayload() : undefined;

        // Start chat request
        const token = tokenManager.getAccessToken();
        console.log('🚀 Sending chat request to agent service...');
        console.log('   Conversation ID:', conversationId || '(new)');
        console.log('   Message:', message.substring(0, 100) + (message.length > 100 ? '...' : ''));
        if (scriptId) console.log('   Script ID:', scriptId);
        if (fileNames.length > 0) console.log('   Files:', fileNames.join(', '));

        const response = await fetch(`${API_URL}/api/chat/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            conversationId: conversationId,
            message,
            context: context || contextPayload?.metadata || {},
            contextPayload,
            scriptId: scriptId || undefined,
          }),
        });

        if (!response.ok) {
          console.error('❌ Chat request failed:', response.status, response.statusText);
          throw new Error('Failed to start chat');
        }

        const data = await response.json();
        console.log('📨 Chat started, conversation ID:', data.conversationId);
        console.log('   Stream URL:', data.streamUrl);

        // Notify parent of new conversation ID
        const effectiveConversationId = data.conversationId || conversationId;
        if (data.conversationId && data.conversationId !== conversationId && onConversationCreated) {
          console.log('🆕 New conversation created, notifying parent:', data.conversationId);
          onConversationCreated(data.conversationId);
        }

        // Upload files now that we have a conversationId (before SSE stream)
        if (files && files.length > 0 && effectiveConversationId) {
          console.log(`📎 Uploading ${files.length} file(s) to conversation ${effectiveConversationId}`);
          for (const file of files) {
            try {
              const formData = new FormData();
              formData.append('file', file);
              formData.append('title', file.name);

              const uploadResponse = await fetch(
                `${API_URL}/api/ai/agent/conversations/${effectiveConversationId}/upload/`,
                {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}` },
                  body: formData,
                }
              );

              if (uploadResponse.ok) {
                const upload: AgentUpload = await uploadResponse.json();
                setUploads((prev) => mergeUploads(prev, [upload]));
                console.log('📎 File uploaded:', upload.title);
              } else {
                console.error('📎 File upload failed:', uploadResponse.statusText);
              }
            } catch (uploadError) {
              console.error('📎 File upload error:', uploadError);
            }
          }
          // Also flush any ref-queued files
          pendingUploadFilesRef.current = [];
        }

        // Connect to SSE stream
        const streamUrl = `${API_URL}${data.streamUrl}`;
        const eventSource = new EventSource(streamUrl, {
          withCredentials: true,
        });

        eventSourceRef.current = eventSource;
        streamingMessageRef.current = '';
        segmentsRef.current = [{ type: 'text', text: '' }];
        streamingIdRef.current = null;

        // Initialize assistant message
        const assistantMessage: AgentMessage = {
          id: data.messageId,
          role: 'assistant',
          content: '',
          timestamp: new Date().toISOString(),
          toolCalls: [],
          segments: [{ type: 'text', text: '' }],
          editIds: [],
          isStreaming: true,
        };
        setMessages((prev) => [...prev, assistantMessage]);

        // Handle token events
        eventSource.addEventListener('token', (event) => {
          const parsed = JSON.parse(event.data);
          streamingMessageRef.current += parsed.data.content;

          // Update the last text segment in segmentsRef
          const segs = segmentsRef.current;
          const lastSeg = segs[segs.length - 1];
          if (lastSeg && lastSeg.type === 'text') {
            lastSeg.text += parsed.data.content;
          } else {
            segs.push({ type: 'text', text: parsed.data.content });
          }

          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === data.messageId
                ? { ...msg, content: streamingMessageRef.current, segments: [...segmentsRef.current] }
                : msg
            )
          );
        });

        // Handle tool call events
        eventSource.addEventListener('tool_call', (event) => {
          const parsed = JSON.parse(event.data);
          const toolCall: ToolCall = {
            id: parsed.data.id,
            tool: parsed.data.tool,
            params: parsed.data.params,
            hidden: parsed.data.hidden === true,
          };

          // Push tool_call segment, then a new empty text segment for subsequent text
          segmentsRef.current.push({ type: 'tool_call', toolCall });
          segmentsRef.current.push({ type: 'text', text: '' });

          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === data.messageId
                ? {
                    ...msg,
                    toolCalls: [...(msg.toolCalls || []), toolCall],
                    segments: [...segmentsRef.current],
                  }
                : msg
            )
          );
        });

        // Handle tool result events
        eventSource.addEventListener('tool_result', (event) => {
          const parsed = JSON.parse(event.data);

          // Update the matching tool_call segment in segmentsRef
          for (const seg of segmentsRef.current) {
            if (seg.type === 'tool_call' && seg.toolCall.id === parsed.data.id) {
              seg.toolCall = { ...seg.toolCall, result: parsed.data.result };
              break;
            }
          }

          setMessages((prev) =>
            prev.map((msg) => {
              if (msg.id !== data.messageId) return msg;

              const toolCalls = (msg.toolCalls || []).map((tc) =>
                tc.id === parsed.data.id
                  ? { ...tc, result: parsed.data.result }
                  : tc
              );

              const editIds = parsed.data.editId
                ? [...(msg.editIds || []), parsed.data.editId]
                : msg.editIds;

              return { ...msg, toolCalls, editIds, segments: [...segmentsRef.current] };
            })
          );
        });

        // Handle undo available events
        eventSource.addEventListener('undo_available', (event) => {
          const parsed = JSON.parse(event.data);
          // Undo info is stored in message editIds
          console.log('Undo available:', parsed.data);
        });

        // Handle data_changed events — dispatch to window so page components can refetch
        eventSource.addEventListener('data_changed', (event) => {
          const parsed = JSON.parse(event.data);
          console.log('🔄 Data changed by agent:', parsed.data);
          window.dispatchEvent(
            new CustomEvent('agentDataChanged', { detail: parsed.data })
          );
        });

        // Handle view suggestion events
        eventSource.addEventListener('view_suggestion', (event) => {
          const parsed = JSON.parse(event.data);
          const suggestion = parsed.data;
          console.log('🎯 Received view_suggestion event:', suggestion);
          
          if (suggestion.autoNavigate) {
            // Auto-navigate immediately
            console.log('✅ Auto-navigating:', suggestion);
            if (onViewSuggestion) {
              onViewSuggestion(suggestion);
            }
          } else {
            // Add to suggestions list for user to click
            console.log('💡 Adding suggestion button:', suggestion);
            setViewSuggestions(prev => [...prev, suggestion]);
          }
        });

        // Handle conversation_event — Celery-driven events (e.g. ResearchJob
        // progress + completion) published via Django signal → Redis → Node
        // subscriber. Two subtypes, distinguished by payload.type:
        //   - research_job_progress: update the pending-job animation
        //   - research_job_update:   terminal (completed|failed), render card
        eventSource.addEventListener('conversation_event', (event) => {
          const parsed = JSON.parse(event.data);
          const payload = parsed.data as ConversationEventPayload;
          const subtype = payload.type;

          if (subtype === 'research_job_progress') {
            const progress: PendingJob = {
              jobId: String(payload.job_id || ''),
              kind: String(payload.kind || ''),
              kindDisplay: String(payload.kind_display || 'Research'),
              status: String(payload.status || 'pending'),
              currentStep: Number(payload.current_step ?? 0),
              totalSteps: Number(payload.total_steps ?? 1),
              currentStepLabel: String(payload.current_step_label || ''),
              startedAt: (payload.started_at as string | null) ?? null,
            };
            setPendingJobs((prev) => {
              const existing = prev.findIndex((j) => j.jobId === progress.jobId);
              if (existing >= 0) {
                const next = [...prev];
                next[existing] = progress;
                return next;
              }
              return [...prev, progress];
            });
            return;
          }

          // Terminal event (research_job_update) — remove from pending + add
          // the durable event card.
          console.log('🔔 Conversation event:', payload);
          if (payload.job_id) {
            setPendingJobs((prev) => prev.filter((j) => j.jobId !== payload.job_id));
          }
          const eventMessage: AgentMessage = {
            id: `event-${payload.job_id || Date.now()}`,
            role: 'event',
            content: payload.summary || payload.kind_display || 'Update',
            timestamp: payload.occurred_at || new Date().toISOString(),
            eventPayload: payload,
          };
          setMessages((prev) => {
            // De-duplicate: the Django signal also persists an AgentMessage,
            // so on page reload we'd see both. Drop if we already have an
            // event with the same job_id.
            if (payload.job_id && prev.some(
              (m) => m.role === 'event' && m.eventPayload?.job_id === payload.job_id
            )) {
              return prev;
            }
            return [...prev, eventMessage];
          });
        });

        // Handle action suggestion events (approval-required actions)
        eventSource.addEventListener('action_suggestion', (event) => {
          const parsed = JSON.parse(event.data);
          const suggestion = parsed.data as ActionSuggestion;
          console.log('🔔 Received action_suggestion event:', suggestion);
          setActionSuggestions(prev => [...prev, suggestion]);
        });

        // Handle complete event
        eventSource.addEventListener('complete', (event) => {
          const parsed = JSON.parse(event.data);
          console.log('✅ Stream complete event received');
          console.log('   Full response length:', parsed.data.fullResponse.length);
          console.log('   Messages should be saved to Django by now');

          // Filter out empty trailing text segments
          const finalSegments = segmentsRef.current.filter(
            (seg: ContentSegment, idx: number) => !(seg.type === 'text' && seg.text === '' && idx === segmentsRef.current.length - 1)
          );

          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === data.messageId
                ? {
                    ...msg,
                    content: parsed.data.fullResponse,
                    segments: finalSegments,
                    isStreaming: false,
                  }
                : msg
            )
          );

          setIsStreaming(false);
          eventSource.close();
          eventSourceRef.current = null;
        });

        // Handle error event
        eventSource.addEventListener('error', (event: any) => {
          const parsed = JSON.parse(event.data || '{}');
          const errorMessage = parsed.data?.message || 'Stream error';

          if (onError) {
            onError(errorMessage);
          }

          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === data.messageId
                ? {
                    ...msg,
                    content: msg.content + `\n\n[Error: ${errorMessage}]`,
                    isStreaming: false,
                  }
                : msg
            )
          );

          setIsStreaming(false);
          eventSource.close();
          eventSourceRef.current = null;
        });

        // Handle connection errors
        eventSource.onerror = (error) => {
          console.error('SSE connection error:', error);
          if (onError) {
            onError('Connection error');
          }
          setIsStreaming(false);
          eventSource.close();
          eventSourceRef.current = null;
        };
      } catch (error: any) {
        console.error('Error sending message:', error);
        if (onError) {
          onError(error.message || 'Failed to send message');
        }
        setIsStreaming(false);
      }
    },
    [conversationId, buildContextPayload, onViewSuggestion, onError, onConversationCreated]
  );

  /**
   * Lightweight poll: reconcile pendingJobs, uploads (promotion/extraction
   * state), and event messages from the conversation-detail endpoint without
   * disturbing messages the live SSE stream is currently writing.
   *
   * Exists because Redis pub/sub only reaches the frontend while an
   * `/api/chat/chat` SSE stream is open. A long-running ResearchJob (e.g.
   * 15-minute deep research) will publish its terminal event into an empty
   * channel if the user's chat turn ended first. The durable
   * AgentMessage(role='event') row is still written — this poll picks it up.
   */
  const reconcileFromBackend = useCallback(async () => {
    if (!conversationId) return;
    try {
      const token = tokenManager.getAccessToken();
      const response = await fetch(
        `${API_URL}/api/chat/conversations/${conversationId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.ok) return;
      const data = await response.json();

      // Reconcile pending jobs: reuse server state as the source of truth.
      const serverPending: PendingJob[] = (data.pending_research_jobs || []).map(
        (j: any): PendingJob => ({
          jobId: String(j.id),
          kind: String(j.kind || ''),
          kindDisplay: String(j.kind_display || 'Research'),
          status: String(j.status || 'pending'),
          currentStep: Number(j.current_step ?? 0),
          totalSteps: Number(j.total_steps ?? 1),
          currentStepLabel: String(j.current_step_label || ''),
          startedAt: j.started_at || null,
          diligenceCompleted: Number(j.diligence_completed ?? 0),
          diligenceTotal: Number(j.diligence_total ?? 0),
        }),
      );
      setPendingJobs(serverPending);

      // Refresh upload promotion / extraction state (merge, so an upload that
      // landed mid-request isn't dropped).
      if (Array.isArray(data.uploads)) {
        setUploads((prev) => mergeUploads(prev, data.uploads as AgentUpload[]));
      }

      // Reconcile event-role messages. Only ADD new ones keyed by id — don't
      // replace streaming messages in the thread that might be mid-render.
      setMessages((prev) => {
        const existingIds = new Set(prev.map((m) => m.id));
        const newEventMessages: AgentMessage[] = (data.messages || [])
          .filter((m: any) => m.role === 'event' && !existingIds.has(m.id))
          .map(
            (m: any): AgentMessage => ({
              id: m.id,
              role: 'event',
              content: m.content,
              timestamp: m.created_at,
              eventPayload: m.event_payload || undefined,
              isStreaming: false,
            }),
          );
        if (newEventMessages.length === 0) return prev;
        return [...prev, ...newEventMessages].sort(
          (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
        );
      });
    } catch (err) {
      // Poll failures are noisy but not actionable — the next tick retries.
      console.warn('Pending-job reconcile failed:', err);
    }
  }, [conversationId]);

  /**
   * Polling fallback for pending jobs and running upload extractions. Runs
   * while there's something outstanding AND no SSE stream is active (live
   * events preferred).
   */
  const uploadsExtracting = uploads.some(isUploadExtracting);
  useEffect(() => {
    const hasOutstanding = pendingJobs.length > 0 || uploadsExtracting;
    if (!hasOutstanding) return;
    if (isStreaming) return;  // live SSE is already covering us
    if (!conversationId) return;

    // Reconcile once immediately so a just-completed job clears fast.
    reconcileFromBackend();

    // Extractions finish in tens of seconds; research jobs take minutes.
    const intervalMs = pendingJobs.length === 0 ? 5000 : 15000;
    const interval = setInterval(reconcileFromBackend, intervalMs);
    return () => clearInterval(interval);
  }, [
    pendingJobs.length,
    uploadsExtracting,
    isStreaming,
    conversationId,
    reconcileFromBackend,
  ]);

  /**
   * Undo an edit
   */
  const undo = useCallback(
    async (editId: string) => {
      try {
        const token = tokenManager.getAccessToken();
        const response = await fetch(`${API_URL}/api/chat/undo`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            conversationId: conversationId,
            editId,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to undo');
        }

        const result = await response.json();

        // Add system message about undo
        const undoMessage: AgentMessage = {
          id: `undo-${Date.now()}`,
          role: 'assistant',
          content: `✓ Undone: ${result.description}`,
          timestamp: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, undoMessage]);

        // Notify page components to refetch (undo is also a data change)
        if (result.resource) {
          window.dispatchEvent(
            new CustomEvent('agentDataChanged', { detail: { resource: result.resource, action: 'updated' } })
          );
        } else {
          // Fallback: infer resource from the tool name in the undo description
          const resource = result.description?.includes('scorecard') ? 'scorecard'
            : result.description?.includes('document') ? 'document' : undefined;
          if (resource) {
            window.dispatchEvent(
              new CustomEvent('agentDataChanged', { detail: { resource, action: 'updated' } })
            );
          }
        }

        return result;
      } catch (error: any) {
        console.error('Error undoing edit:', error);
        if (onError) {
          onError(error.message || 'Failed to undo');
        }
        throw error;
      }
    },
    [conversationId, onError]
  );

  /**
   * Start a new conversation
   */
  const startNewConversation = useCallback(() => {
    console.log('🆕 Starting fresh conversation in hook');
    setMessages([]);
    setViewSuggestions([]);
    setActionSuggestions([]);
    setPendingJobs([]);
    setUploads([]);
    // conversationId is managed by parent, we just clear local state
  }, []);

  /**
   * Load a conversation
   */
  const loadConversation = useCallback(
    async (conversationId: string) => {
      try {
        console.log('📖 Loading conversation from backend:', conversationId);
        const token = tokenManager.getAccessToken();
        const response = await fetch(
          `${API_URL}/api/chat/conversations/${conversationId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          if (response.status === 404) {
            console.warn('⚠️ Conversation not found (may have been deleted):', conversationId);
            // Clear local state - parent manages the ID
            setMessages([]);
            return;
          }
          console.error('❌ Failed to load conversation:', response.status, response.statusText);
          throw new Error('Failed to load conversation');
        }

        const data = await response.json();
        console.log('✅ Loaded conversation:', data);
        // conversationId is managed by parent, just load messages.
        // Legacy rows from the retired in-chat interview accessory are
        // skipped — interviews live on the module page now: role='question'
        // rows, and the inert "(interview answer submitted)" user turns its
        // silent continue ping wrote.
        setMessages(
          data.messages
            .filter((msg: any) => msg.role !== 'question'
              && !(msg.role === 'user' && msg.content === LEGACY_INTERVIEW_PING))
            .map((msg: any): AgentMessage => {
            // Event-role messages are Phase 3 inline notification cards;
            // they have no tool calls and are rendered specially.
            if (msg.role === 'event') {
              return {
                id: msg.id,
                role: 'event',
                content: msg.content,
                timestamp: msg.created_at,
                eventPayload: msg.event_payload || undefined,
                isStreaming: false,
              };
            }
            const toolCalls: ToolCall[] = msg.tool_calls || [];
            // Synthesize segments from content + toolCalls for historical messages
            const segments: ContentSegment[] = [];
            if (msg.content) {
              segments.push({ type: 'text', text: msg.content });
            }
            for (const tc of toolCalls) {
              segments.push({ type: 'tool_call', toolCall: tc });
            }
            return {
              id: msg.id,
              role: msg.role,
              content: msg.content,
              timestamp: msg.created_at,
              toolCalls,
              segments: segments.length > 0 ? segments : undefined,
              editIds: msg.edit_ids,
              isStreaming: false,
            };
          })
        );

        // Hydrate the conversation's uploads so the attachment chips (and
        // their program-context toggle) survive a reload or a hand-off from
        // another chat surface (e.g. /home → drawer).
        setUploads(Array.isArray(data.uploads) ? (data.uploads as AgentUpload[]) : []);

        // Hydrate any pending ResearchJobs so the in-flight animation is
        // visible immediately on reload. The first research_job_progress SSE
        // event from the worker will refresh these values.
        const pending = (data.pending_research_jobs || []).map((j: any): PendingJob => ({
          jobId: String(j.id),
          kind: String(j.kind || ''),
          kindDisplay: String(j.kind_display || 'Research'),
          status: String(j.status || 'pending'),
          currentStep: Number(j.current_step ?? 0),
          totalSteps: Number(j.total_steps ?? 1),
          currentStepLabel: String(j.current_step_label || ''),
          startedAt: j.started_at || null,
          diligenceCompleted: Number(j.diligence_completed ?? 0),
          diligenceTotal: Number(j.diligence_total ?? 0),
        }));
        setPendingJobs(pending);

      } catch (error: any) {
        console.error('Error loading conversation:', error);
        if (onError) {
          onError(error.message || 'Failed to load conversation');
        }
      }
    },
    [onError]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      closeStream();
    };
  }, [closeStream]);

  const dismissSuggestion = useCallback((index: number) => {
    setViewSuggestions(prev => prev.filter((_, i) => i !== index));
  }, []);

  const clearSuggestions = useCallback(() => {
    setViewSuggestions([]);
  }, []);

  const dismissActionSuggestion = useCallback((index: number) => {
    setActionSuggestions(prev => prev.filter((_, i) => i !== index));
  }, []);

  const clearActionSuggestions = useCallback(() => {
    setActionSuggestions([]);
  }, []);

  /**
   * Upload a file to the current conversation.
   * If no conversation exists yet, queues the file for upload after creation.
   */
  const uploadFile = useCallback(
    async (file: File): Promise<AgentUpload | null> => {
      if (!conversationId) {
        console.log('📎 Queuing file for upload after conversation creation:', file.name);
        pendingUploadFilesRef.current = [...pendingUploadFilesRef.current, file];
        return null;
      }

      try {
        const token = tokenManager.getAccessToken();
        const formData = new FormData();
        formData.append('file', file);
        formData.append('title', file.name);

        const response = await fetch(
          `${API_URL}/api/ai/agent/conversations/${conversationId}/upload/`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          }
        );

        if (!response.ok) {
          throw new Error(`Upload failed: ${response.statusText}`);
        }

        const upload: AgentUpload = await response.json();
        setUploads((prev) => mergeUploads(prev, [upload]));
        console.log('📎 File uploaded:', upload.title);
        return upload;
      } catch (error: any) {
        console.error('Error uploading file:', error);
        if (onError) {
          onError(error.message || 'Failed to upload file');
        }
        return null;
      }
    },
    [conversationId, onError]
  );

  /**
   * Flip an upload between "Program context" and "Chat only". The backend
   * promotes / demotes the linked ProgramContextDocument when the conversation
   * is program-bound; local state is replaced with the server's view.
   */
  const setUploadProgramContext = useCallback(
    async (uploadId: string, useAsProgramContext: boolean): Promise<AgentUpload | null> => {
      if (!conversationId) return null;
      // Optimistic flip so the toggle feels immediate; reverted on failure.
      setUploads((prev) =>
        prev.map((u) => (u.id === uploadId ? { ...u, use_as_program_context: useAsProgramContext } : u)),
      );
      try {
        const token = tokenManager.getAccessToken();
        const response = await fetch(
          `${API_URL}/api/ai/agent/conversations/${conversationId}/uploads/${uploadId}/`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ use_as_program_context: useAsProgramContext }),
          },
        );
        if (!response.ok) {
          throw new Error(`Could not update file (${response.status})`);
        }
        const updated: AgentUpload = await response.json();
        setUploads((prev) => mergeUploads(prev, [updated]));
        // A promoted/demoted doc changes the program's context-documents list.
        window.dispatchEvent(
          new CustomEvent('agentDataChanged', {
            detail: { resource: 'context_document', action: 'updated' },
          }),
        );
        return updated;
      } catch (error: any) {
        console.error('Error updating upload:', error);
        setUploads((prev) =>
          prev.map((u) => (u.id === uploadId ? { ...u, use_as_program_context: !useAsProgramContext } : u)),
        );
        if (onError) {
          onError(error.message || 'Failed to update file');
        }
        return null;
      }
    },
    [conversationId, onError],
  );

  // Flush pending uploads when conversationId becomes available
  useEffect(() => {
    if (conversationId && pendingUploadFilesRef.current.length > 0) {
      const filesToUpload = [...pendingUploadFilesRef.current];
      pendingUploadFilesRef.current = [];
      console.log(`📎 Flushing ${filesToUpload.length} pending upload(s) for conversation ${conversationId}`);

      (async () => {
        for (const file of filesToUpload) {
          await uploadFile(file);
        }
      })();
    }
  }, [conversationId, uploadFile]);

  return {
    messages,
    isStreaming,
    conversationId, // Just pass through the prop
    viewSuggestions,
    actionSuggestions,
    uploads,
    pendingJobs,
    sendMessage,
    uploadFile,
    setUploadProgramContext,
    undo,
    dismissSuggestion,
    clearSuggestions,
    dismissActionSuggestion,
    clearActionSuggestions,
    startNewConversation,
    loadConversation,
  };
};
